import React from 'react';
import { observer } from 'mobx-react';

import Lowlight from 'react-lowlight';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { renderName, makeNative, appStore } from '../store';
import { mdify } from '../syntaxHighlighting';


const renderParams = (native) => {
  if (native?.params?.[0]?.description) {
    return (
      <div className="parameters">
        <strong>Parameters:</strong><br /><ul>
          {
            native.params.map((param) => (
              <li key={param.name}><strong>{param.name}</strong>: {mdify(param.description, true)}</li>
            ))
          }
        </ul>
      </div>
    );
  }

  return '';
};

const renderReturns = (native) => {
  if (native.resultsDescription) {
    return (
      <div className="returns">
        <strong>Returns: </strong><div>{mdify(native.resultsDescription)}</div>
      </div>
    )
  }
};

const renderDescription = (native) => {
  if (native.description) {
    return (
      <div className="desc">
        {renderParams(native)}
        {renderReturns(native)}
        {mdify(native.description)}
      </div>
    );
  }

  return null;
};

const renderApiSet = (native) => {
  if (native.apiset && native.apiset[0]) {
    return (
      <>
        <span>API set:&nbsp;</span>
        {native.apiset}
      </>
    );
  }

  return null;
};

const renderAliases = (native) => {
  return (native.aliases || []).map(a => !a.startsWith('0x') ? (<p className="old" key={a}><strong>Old name: </strong> {a}</p>) : '');
};

const ExampleCode = ({ lang, code }) => (
  <Lowlight language={lang} value={code} />
);

const langList = {
  cs: 'C#',
  lua: 'Lua',
  js: 'JavaScript'
};

const mapLanguageName = (lang) => langList[lang] || lang;

const Examples = ({ set }) => {
  const lastLang = window.localStorage.getItem('lastLang');
  const initIndex = set.findIndex(example => example.lang === lastLang);

  const [index, setIndex] = React.useState(initIndex < 0 ? 0 : initIndex);

  const setIndexWrap = (index) => {
    window.localStorage.setItem('lastLang', set[index].lang);

    setIndex(index);
  };

  return (
    <Tabs selectedIndex={index} onSelect={index => setIndexWrap(index)}>
      <TabList>
        {set.map(example => (<Tab key={example.lang}>{mapLanguageName(example.lang)}</Tab>))}
      </TabList>
      {set.map(example => (<TabPanel key={example.lang}>
        <ExampleCode lang={example.lang} code={example.code} />
      </TabPanel>))}
    </Tabs>
  );
};

const renderExamples = (native) => {
  const examples = native.examples || [];
  const exampleHeading = (examples.length > 0) ? (<p><strong>Examples:</strong></p>) : '';

  // using key to reinstance per native
  const exampleList = (examples.length > 0) ? (<Examples key={native.hash} set={examples} />) : '';

  return (
    <div className="examples">
      {exampleHeading}
      {exampleList}
    </div>
  );
}

const getNativeRoot = (native) => {
  if (native.ns !== 'CFX') {
    return `https://github.com/citizenfx/natives/edit/master/${native.ns}/`;
  }

  return `https://github.com/citizenfx/fivem/edit/master/ext/native-decls/`;
};

const Native = observer((props) => {
  const { language } = props;
  const { native } = appStore.selection.curNative;

  const nativeName = language.renderName(native);

  return (
    <div className="details">
      <div className="info">
        <span className="namespace">
          <span>Namespace:&nbsp;</span>
          {native.namespace}
        </span>
        <span className="apiset">
          {renderApiSet(native)}
        </span>
        <a className="icon-link" target="_blank" href={`${getNativeRoot(native)}${makeNative(renderName(native))}.md`}>
          <span className="material-icons-outlined">edit</span>
          Improve
        </a>
      </div>

      <h2 title={nativeName}>
        {nativeName}
      </h2>

      <div className="hash">
        <span><pre>{native.hash}</pre></span>
        <span><pre>{native.jhash}</pre></span>
      </div>

      <div className="code">
        <Lowlight language={language.lowlightName} value={language.formatNativeCode(native)} />
      </div>

      {renderDescription(native)}
      {renderAliases(native)}
      {renderExamples(native)}
    </div>
  );
});

export const NativeDetail = observer(({ language }) => {
  const nativeUi = appStore.selection.curNative
    ? <Native language={language} />
    : <div className="empty">Please select a native from the list.</div>;

  return (
    <aside>
      {nativeUi}
    </aside>
  );
});
